import { Injectable, inject, signal, computed, OnDestroy } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Subject, firstValueFrom, takeUntil } from 'rxjs';
import { environment } from 'src/environments/environment';
import { WebSocketService } from './websocket.service';
import { MembershipTierService } from './membership-tier.service';
import { isProRequiredError } from '../utils/http-error-utils';
import {
  AgremoAnalysis,
  AgremoAnalysisStatus,
  AgremoAnalysisType,
  AgremoAnalysisFile,
  AgremoMap,
  AgremoMapStatus,
  AgremoProgressEvent,
  AgremoCompleteEvent,
  AgremoFailedEvent,
  AgremoMapStatusEvent,
  CreateAnalysisDto,
  CreateMapDto
} from '../models/agremo-analysis.model';

/**
 * API Response wrapper
 */
interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  total?: number;
  page?: number;
  limit?: number;
}

/**
 * Pre-signed upload URL response
 */
interface UploadUrlResponse {
  uploadUrl: string;
  s3Key: string;
  s3Url: string;
  fileName: string;
}

/**
 * Agremo Service
 *
 * Frontend service for Agremo analysis management.
 * Communicates with the backend API and handles real-time updates via WebSocket.
 *
 * Features:
 * - List, create, and view analyses
 * - File upload via pre-signed S3 URLs
 * - Real-time progress updates via WebSocket
 * - Signal-based reactive state management
 *
 * Reference: /agent-os/specs/2026-01-22-agremo-api-integration/spec.md
 */
@Injectable({
  providedIn: 'root'
})
export class AgremoService implements OnDestroy {
  private http = inject(HttpClient);
  private webSocketService = inject(WebSocketService);
  private membershipTierService = inject(MembershipTierService);
  private apiUrl = environment.apiUrl;
  private destroy$ = new Subject<void>();

  // Signal-based state
  private analysesSignal = signal<AgremoAnalysis[]>([]);
  private analysisTypesSignal = signal<AgremoAnalysisType[]>([]);
  private loadingSignal = signal<boolean>(false);
  private errorSignal = signal<string | null>(null);
  // True when the ORG'S tier dropped to Core between the page loading and
  // this call landing (e.g. a subscription cancelled directly in Stripe,
  // outside any ADN-driven flow that would have refreshed the cached tier
  // signal — see ADN-2286's TG12 lifecycle E2E). Distinct from errorSignal
  // so the component can redirect to the preview page instead of rendering
  // a raw HTTP-failure banner. See loadAnalyses's 403 branch.
  private proRequiredSignal = signal<boolean>(false);
  private totalCountSignal = signal<number>(0);
  private currentPageSignal = signal<number>(1);
  private pageSizeSignal = signal<number>(20);
  private configuredSignal = signal<boolean | null>(null); // null = unknown (not yet checked)

  // Public computed signals
  readonly analyses = computed(() => this.analysesSignal());
  readonly analysisTypes = computed(() => this.analysisTypesSignal());
  readonly isLoading = computed(() => this.loadingSignal());
  readonly error = computed(() => this.errorSignal());
  readonly proRequired = computed(() => this.proRequiredSignal());
  readonly totalCount = computed(() => this.totalCountSignal());
  readonly isConfigured = computed(() => this.configuredSignal());
  readonly currentPage = computed(() => this.currentPageSignal());
  readonly pageSize = computed(() => this.pageSizeSignal());

  // Computed stats
  readonly processingAnalyses = computed(() =>
    this.analysesSignal().filter(a =>
      a.status === 'processing' || a.status === 'queued' || a.status === 'downloading'
    )
  );

  readonly completedAnalyses = computed(() =>
    this.analysesSignal().filter(a => a.status === 'completed')
  );

  readonly failedAnalyses = computed(() =>
    this.analysesSignal().filter(a => a.status === 'failed')
  );

  // In-flight promise for checkStatus to prevent concurrent HTTP requests
  private checkStatusPromise: Promise<boolean | null> | null = null;

  // Track WebSocket subscription state
  private isSubscribed = false;

  constructor() {
    // Subscribe to WebSocket events when connection state changes to authenticated
    this.webSocketService.connectionState$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(state => {
      if (state === 'authenticated' && !this.isSubscribed) {
        this.setupWebSocketSubscriptions();
      } else if (state === 'disconnected') {
        this.isSubscribed = false;
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ============================================
  // WEBSOCKET SUBSCRIPTIONS
  // ============================================

  /**
   * Set up WebSocket subscriptions for real-time Agremo analysis updates.
   * Listens for progress, complete, and failed events.
   */
  private setupWebSocketSubscriptions(): void {
    if (this.isSubscribed) return;
    this.isSubscribed = true;

    console.log('[AgremoService] Setting up WebSocket subscriptions for analysis events');

    // Subscribe to progress events
    this.webSocketService.on<AgremoProgressEvent>('agremo-analysis:progress')
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => {
        console.log('[AgremoService] Received agremo-analysis:progress:', event);
        this.handleProgressEvent(event);
      });

    // Subscribe to complete events
    this.webSocketService.on<AgremoCompleteEvent>('agremo-analysis:complete')
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => {
        console.log('[AgremoService] Received agremo-analysis:complete:', event);
        this.handleCompleteEvent(event);
      });

    // Subscribe to failed events
    this.webSocketService.on<AgremoFailedEvent>('agremo-analysis:failed')
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => {
        console.log('[AgremoService] Received agremo-analysis:failed:', event);
        this.handleFailedEvent(event);
      });

    console.log('[AgremoService] WebSocket subscriptions established');
  }

  /**
   * Handle progress event from WebSocket
   */
  private handleProgressEvent(event: AgremoProgressEvent): void {
    this.analysesSignal.update(analyses => {
      return analyses.map(analysis => {
        if (analysis.id === event.analysisId) {
          return {
            ...analysis,
            status: event.status,
            progress: event.progress
          };
        }
        return analysis;
      });
    });
  }

  /**
   * Handle complete event from WebSocket
   */
  private handleCompleteEvent(event: AgremoCompleteEvent): void {
    this.analysesSignal.update(analyses => {
      return analyses.map(analysis => {
        if (analysis.id === event.analysisId) {
          return {
            ...analysis,
            status: 'completed' as AgremoAnalysisStatus,
            progress: 100,
            files: event.files || analysis.files
          };
        }
        return analysis;
      });
    });
  }

  /**
   * Handle failed event from WebSocket
   */
  private handleFailedEvent(event: AgremoFailedEvent): void {
    this.analysesSignal.update(analyses => {
      return analyses.map(analysis => {
        if (analysis.id === event.analysisId) {
          return {
            ...analysis,
            status: 'failed' as AgremoAnalysisStatus,
            errorMessage: event.error,
            progress: 0
          };
        }
        return analysis;
      });
    });
  }

  // ============================================
  // API METHODS
  // ============================================

  /**
   * Check if Agremo integration is configured on the backend.
   * Caches the result in a signal so it only needs one API call per session.
   * Uses in-flight promise caching so concurrent callers share a single HTTP request.
   */
  async checkStatus(): Promise<boolean | null> {
    if (this.configuredSignal() !== null) {
      return this.configuredSignal();
    }
    // If a request is already in flight, share it instead of firing another
    if (this.checkStatusPromise) {
      return this.checkStatusPromise;
    }
    this.checkStatusPromise = this.fetchStatus();
    try {
      return await this.checkStatusPromise;
    } finally {
      this.checkStatusPromise = null;
    }
  }

  private async fetchStatus(): Promise<boolean | null> {
    try {
      const response = await firstValueFrom(
        this.http.get<ApiResponse<{ configured: boolean }>>(`${this.apiUrl}/user/agremo/status`)
      );
      const configured = response?.data?.configured ?? false;
      this.configuredSignal.set(configured);
      return configured;
    } catch {
      // Set to false so the UI transitions deterministically (no stuck spinner).
      // A page reload will re-check.
      console.warn('[AgremoService] Failed to check Agremo status, defaulting to not configured');
      this.configuredSignal.set(false);
      return false;
    }
  }

  /**
   * Load analyses into signal state.
   * Supports optional mapId filter to narrow results to a specific map.
   */
  async loadAnalyses(options?: {
    page?: number;
    limit?: number;
    status?: AgremoAnalysisStatus;
    mapId?: string;
  }): Promise<void> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);
    // Reused component instance could have a stale locked flag from a prior
    // Core-org load (e.g. an org switch without a route change) — same
    // reasoning as maintenance-dashboard.component.ts's proFeaturesLocked.
    this.proRequiredSignal.set(false);

    try {
      let params = new HttpParams();
      if (options?.page) params = params.set('page', options.page.toString());
      if (options?.limit) params = params.set('limit', options.limit.toString());
      if (options?.status) params = params.set('status', options.status);
      if (options?.mapId) params = params.set('mapId', options.mapId);

      const response = await firstValueFrom(
        this.http.get<ApiResponse<AgremoAnalysis[]>>(
          `${this.apiUrl}/user/agremo/analyses`,
          { params }
        )
      );

      if (response.success && response.data) {
        this.analysesSignal.set(response.data.map(a => this.mapApiToAnalysis(a)));
        this.totalCountSignal.set(response.total ?? 0);
        this.currentPageSignal.set(response.page ?? 1);
        this.pageSizeSignal.set(response.limit ?? 20);
      } else {
        throw new Error(response.error || 'Failed to load analyses');
      }
    } catch (error: any) {
      if (isProRequiredError(error)) {
        // Expected outcome when the org's tier dropped to Core underneath an
        // already-open session (the cached tier signal only refreshes on
        // app-load / Checkout-return — see MembershipTierService's
        // docstring). Not an error worth logging or showing raw — the
        // component redirects to the preview page instead. Re-fetching the
        // tier here (rather than just locally) is what lets the sidebar and
        // every OTHER guarded route self-correct too, without waiting for
        // the user to stumble onto a page (like Manage Subscription) that
        // happens to force a refresh.
        this.proRequiredSignal.set(true);
        this.analysesSignal.set([]);
        this.totalCountSignal.set(0);
        void this.membershipTierService.fetchStatus();
        return;
      }
      console.error('Error loading analyses:', error);
      this.errorSignal.set(error.message || 'Failed to load analyses');
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /**
   * Get a single analysis by ID
   */
  async getAnalysisById(id: string): Promise<AgremoAnalysis | null> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    try {
      const response = await firstValueFrom(
        this.http.get<ApiResponse<AgremoAnalysis>>(
          `${this.apiUrl}/user/agremo/analyses/${id}`
        )
      );

      if (response.success && response.data) {
        const analysis = this.mapApiToAnalysis(response.data);
        // Update local state with refreshed data
        this.analysesSignal.update(analyses => {
          const index = analyses.findIndex(a => a.id === id);
          if (index !== -1) {
            const updated = [...analyses];
            updated[index] = analysis;
            return updated;
          }
          return analyses;
        });
        return analysis;
      }
      return null;
    } catch (error: any) {
      console.error('Error fetching analysis:', error);
      this.errorSignal.set(error.message || 'Failed to fetch analysis');
      return null;
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /**
   * Create a new analysis request
   */
  async createAnalysis(dto: CreateAnalysisDto): Promise<AgremoAnalysis | null> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    try {
      const response = await firstValueFrom(
        this.http.post<ApiResponse<AgremoAnalysis>>(
          `${this.apiUrl}/user/agremo/analyses`,
          dto
        )
      );

      if (response.success && response.data) {
        const newAnalysis = this.mapApiToAnalysis(response.data);
        this.analysesSignal.update(analyses => [newAnalysis, ...analyses]);
        this.totalCountSignal.update(count => count + 1);
        return newAnalysis;
      } else {
        throw new Error(response.error || 'Failed to create analysis');
      }
    } catch (error: any) {
      console.error('Error creating analysis:', error);
      this.errorSignal.set(error.message || 'Failed to create analysis');
      return null;
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /**
   * Load analysis types
   */
  async loadAnalysisTypes(): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.http.get<ApiResponse<AgremoAnalysisType[]>>(
          `${this.apiUrl}/user/agremo/analysis-types`
        )
      );

      if (response.success && response.data) {
        this.analysisTypesSignal.set(response.data);
      }
    } catch (error: any) {
      console.error('Error loading analysis types:', error);
    }
  }

  /**
   * Get a pre-signed URL for imagery upload
   */
  async getImageryUploadUrl(filename: string): Promise<UploadUrlResponse> {
    return firstValueFrom(
      this.http.post<UploadUrlResponse>(
        `${this.apiUrl}/file-uploads/agremo-imagery`,
        { filename }
      )
    );
  }

  /**
   * Upload a file to S3 using a pre-signed URL.
   * Agremo imagery is public-read so Agremo can fetch it directly.
   */
  async uploadToS3(file: File, uploadUrl: string): Promise<void> {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
        'x-amz-acl': 'public-read',
      },
    });

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.statusText}`);
    }
  }

  /**
   * Upload imagery file and return S3 URL
   */
  async uploadImagery(file: File): Promise<{ s3Url: string; s3Key: string }> {
    const urlInfo = await this.getImageryUploadUrl(file.name);
    await this.uploadToS3(file, urlInfo.uploadUrl);
    return { s3Url: urlInfo.s3Url, s3Key: urlInfo.s3Key };
  }

  // ============================================
  // MAP API METHODS
  // ============================================

  /**
   * Load maps for a field
   */
  async loadMaps(fieldId: string): Promise<AgremoMap[]> {
    try {
      const params = new HttpParams().set('fieldId', fieldId);
      const response = await firstValueFrom(
        this.http.get<ApiResponse<AgremoMap[]>>(
          `${this.apiUrl}/user/agremo/maps`,
          { params }
        )
      );

      if (response.success && response.data) {
        return response.data.map(m => this.mapApiToMap(m));
      }
      return [];
    } catch (error: any) {
      console.error('Error loading maps:', error);
      return [];
    }
  }

  /**
   * Get ready maps for a field (convenience filter)
   */
  async getReadyMaps(fieldId: string): Promise<AgremoMap[]> {
    try {
      const params = new HttpParams()
        .set('fieldId', fieldId)
        .set('status', 'ready');
      const response = await firstValueFrom(
        this.http.get<ApiResponse<AgremoMap[]>>(
          `${this.apiUrl}/user/agremo/maps`,
          { params }
        )
      );

      if (response.success && response.data) {
        return response.data.map(m => this.mapApiToMap(m));
      }
      return [];
    } catch (error: any) {
      console.error('Error loading ready maps:', error);
      return [];
    }
  }

  /**
   * Create a new map (upload imagery + queue processing)
   */
  async createMap(dto: CreateMapDto): Promise<AgremoMap | null> {
    try {
      const response = await firstValueFrom(
        this.http.post<ApiResponse<AgremoMap>>(
          `${this.apiUrl}/user/agremo/maps`,
          dto
        )
      );

      if (response.success && response.data) {
        return this.mapApiToMap(response.data);
      }
      throw new Error(response.error || 'Failed to create map');
    } catch (error: any) {
      console.error('Error creating map:', error);
      throw error;
    }
  }

  /**
   * Delete a map
   */
  async deleteMap(mapId: string): Promise<void> {
    await firstValueFrom(
      this.http.delete<ApiResponse<void>>(
        `${this.apiUrl}/user/agremo/maps/${mapId}`
      )
    );
  }

  /**
   * Recheck a failed map's readiness in Agremo
   */
  async recheckMap(mapId: string): Promise<{ mapId: string; message: string }> {
    const response = await firstValueFrom(
      this.http.post<ApiResponse<{ mapId: string; message: string }>>(
        `${this.apiUrl}/user/agremo/maps/${mapId}/recheck`, {}
      )
    );
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error || 'Failed to recheck map');
  }

  // ============================================
  // HELPER METHODS
  // ============================================

  /**
   * Map API response to AgremoAnalysis interface.
   * Handles both snake_case and camelCase field names.
   * Includes boundaryGeojson from the analysis detail response.
   */
  private mapApiToAnalysis(data: any): AgremoAnalysis {
    return {
      id: data.id,
      organizationId: data.organizationId ?? data.organization_id,
      mapId: data.mapId ?? data.map_id,
      agremoAnalysisId: data.agremoAnalysisId ?? data.agremo_analysis_id,
      analysisTypeId: data.analysisTypeId ?? data.analysis_type_id,
      analysisTypeName: data.analysisTypeName ?? data.analysis_type_name,
      status: data.status ?? 'pending',
      progress: data.progress ?? 0,
      requestedAt: data.requestedAt ?? data.requested_at ?? data.createdAt ?? data.created_at,
      completedAt: data.completedAt ?? data.completed_at,
      errorMessage: data.errorMessage ?? data.error_message,
      creditsConsumed: data.creditsConsumed ?? data.credits_consumed,
      analysisParams: data.analysisParams ?? data.analysis_params,
      fieldId: data.fieldId ?? data.field_id,
      fieldName: data.fieldName ?? data.field_name,
      customerId: data.customerId ?? data.customer_id,
      customerName: data.customerName ?? data.customer_name,
      statistics: data.statistics ?? undefined,
      boundaryGeojson: data.boundaryGeojson ?? data.boundary_geojson ?? undefined,
      files: data.files?.map((f: any) => this.mapApiToFile(f)) ?? [],
      createdAt: data.createdAt ?? data.created_at,
      updatedAt: data.updatedAt ?? data.updated_at
    };
  }

  /**
   * Map API response to AgremoMap interface.
   * Includes analysisCount from the enriched maps list response.
   */
  private mapApiToMap(data: any): AgremoMap {
    return {
      id: data.id,
      fieldId: data.fieldId ?? data.field_id,
      organizationId: data.organizationId ?? data.organization_id,
      mapName: data.mapName ?? data.map_name ?? '',
      mapDate: data.mapDate ?? data.map_date ?? '',
      cameraType: data.cameraType ?? data.camera_type ?? 'RGB',
      status: data.status ?? 'uploading',
      errorMessage: data.errorMessage ?? data.error_message ?? null,
      analysisCount: data.analysisCount ?? data.analysis_count ?? 0,
      createdAt: data.createdAt ?? data.created_at,
      updatedAt: data.updatedAt ?? data.updated_at,
    };
  }

  /**
   * Map API response to AgremoAnalysisFile interface
   */
  private mapApiToFile(data: any): AgremoAnalysisFile {
    return {
      id: data.id,
      analysisId: data.analysisId ?? data.analysis_id,
      fileType: data.fileType ?? data.file_type,
      agremoUrl: data.agremoUrl ?? data.agremo_url,
      adnUrl: data.adnUrl ?? data.adn_url,
      downloadUrl: data.downloadUrl ?? data.download_url,
      downloadStatus: data.downloadStatus ?? data.download_status,
      downloadedAt: data.downloadedAt ?? data.downloaded_at
    };
  }

  /**
   * Get analysis by ID from local state
   */
  getAnalysisFromState(id: string): AgremoAnalysis | undefined {
    return this.analysesSignal().find(a => a.id === id);
  }

  /**
   * Get human-readable status text
   */
  getStatusText(status: AgremoAnalysisStatus): string {
    switch (status) {
      case 'pending': return 'Pending';
      case 'queued': return 'Queued';
      case 'processing': return 'Processing';
      case 'downloading': return 'Downloading Results';
      case 'completed': return 'Completed';
      case 'failed': return 'Failed';
      default: return status;
    }
  }

  /**
   * Get Bootstrap badge class for status
   */
  getStatusBadgeClass(status: AgremoAnalysisStatus): string {
    switch (status) {
      case 'pending': return 'bg-secondary';
      case 'queued': return 'bg-info text-dark';
      case 'processing': return 'bg-warning text-dark';
      case 'downloading': return 'bg-info text-dark';
      case 'completed': return 'bg-success';
      case 'failed': return 'bg-danger';
      default: return 'bg-secondary';
    }
  }

  /**
   * Get Bootstrap icon class for status
   */
  getStatusIcon(status: AgremoAnalysisStatus): string {
    switch (status) {
      case 'pending': return 'bi-clock';
      case 'queued': return 'bi-hourglass-split';
      case 'processing': return 'bi-arrow-repeat';
      case 'downloading': return 'bi-cloud-arrow-down';
      case 'completed': return 'bi-check-circle';
      case 'failed': return 'bi-exclamation-triangle';
      default: return 'bi-question-circle';
    }
  }

  /**
   * Get file type label
   */
  getFileTypeLabel(fileType: string): string {
    switch (fileType) {
      case 'pdf': return 'PDF Report';
      case 'tiff': return 'GeoTIFF Imagery';
      case 'shp': return 'Shapefile';
      case 'png': return 'PNG Image';
      default: return fileType.toUpperCase();
    }
  }

  /**
   * Get file type icon
   */
  getFileTypeIcon(fileType: string): string {
    switch (fileType) {
      case 'pdf': return 'bi-file-earmark-pdf';
      case 'tiff': return 'bi-file-earmark-image';
      case 'shp': return 'bi-file-earmark-zip';
      case 'png': return 'bi-file-earmark-image';
      default: return 'bi-file-earmark';
    }
  }

  /**
   * Get human-readable map status text
   */
  getMapStatusText(status: AgremoMapStatus): string {
    switch (status) {
      case 'uploading': return 'Uploading';
      case 'processing': return 'Processing';
      case 'ready': return 'Ready';
      case 'failed': return 'Failed';
      default: return status;
    }
  }

  /**
   * Get Bootstrap badge class for map status
   */
  getMapStatusBadgeClass(status: AgremoMapStatus): string {
    switch (status) {
      case 'uploading': return 'bg-info text-dark';
      case 'processing': return 'bg-warning text-dark';
      case 'ready': return 'bg-success';
      case 'failed': return 'bg-danger';
      default: return 'bg-secondary';
    }
  }

  /**
   * Clear error state
   */
  clearError(): void {
    this.errorSignal.set(null);
  }
}
